// INSIDO (16-mensagens-de-voz): voice note filename contract. A voice
// message is a normal message with one Opus/WebM attachment whose filename
// carries the metadata the bubble needs without touching the upstream model:
//
//   voz-<unixms>-<durationMs>ms-<peaks>.webm
//
// where <peaks> is 48 bytes (0..255) in unpadded base64url (64 chars). The
// legacy/short form `voz-<unixms>-<durationMs>ms.webm` (no peaks) also parses.
// No DOM / Solid imports (node contract-tested).

/** Filename prefix marking a voice message attachment */
export const VOICE_PREFIX = "voz-";

/** Bars stored in the filename */
export const VOICE_PEAK_COUNT = 48;

const ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

const PATTERN =
  /^voz-(\d{1,15})-(\d{1,9})ms(?:-([A-Za-z0-9_-]+))?\.(webm|ogg|mp4|m4a)$/;

/** Parsed voice filename */
export interface VoiceFileInfo {
  /** Recording time (unix ms) */
  timestamp: number;
  /** Exact duration (ms) */
  durationMs: number;
  /** Peaks 0..255 (one per bar), if present and valid */
  peaks?: number[];
  /** File extension */
  extension: string;
}

/**
 * Encode bytes (0..255) as unpadded base64url.
 * @param bytes Byte values
 * @returns base64url string
 */
export function encodePeaks(bytes: number[]): string {
  const b = bytes.map((v) =>
    Math.min(255, Math.max(0, Math.round(Number.isFinite(v) ? v : 0))),
  );
  let out = "";
  for (let i = 0; i < b.length; i += 3) {
    const n = (b[i] << 16) | ((b[i + 1] ?? 0) << 8) | (b[i + 2] ?? 0);
    out += ALPHABET[(n >> 18) & 63] + ALPHABET[(n >> 12) & 63];
    if (i + 1 < b.length) out += ALPHABET[(n >> 6) & 63];
    if (i + 2 < b.length) out += ALPHABET[n & 63];
  }
  return out;
}

/**
 * Decode unpadded base64url into bytes; `undefined` on invalid input.
 * @param text base64url string
 * @returns Byte values
 */
export function decodePeaks(text: string): number[] | undefined {
  if (text.length % 4 === 1) return undefined;
  const out: number[] = [];
  let buffer = 0;
  let bits = 0;
  for (const char of text) {
    const value = ALPHABET.indexOf(char);
    if (value < 0) return undefined;
    buffer = (buffer << 6) | value;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out.push((buffer >> bits) & 255);
    }
  }
  return out;
}

/**
 * Build the filename for a voice note.
 * @param info Timestamp, duration and peaks (0..255; padded / cut to 48)
 * @returns Filename
 */
export function buildVoiceFilename(info: {
  timestamp: number;
  durationMs: number;
  peaks?: number[];
  extension?: string;
}): string {
  const ts = Math.max(0, Math.floor(info.timestamp));
  const dur = Math.max(0, Math.round(info.durationMs));
  const ext = info.extension ?? "webm";
  if (!info.peaks) return `${VOICE_PREFIX}${ts}-${dur}ms.${ext}`;
  const peaks = info.peaks.slice(0, VOICE_PEAK_COUNT);
  while (peaks.length < VOICE_PEAK_COUNT) peaks.push(0);
  return `${VOICE_PREFIX}${ts}-${dur}ms-${encodePeaks(peaks)}.${ext}`;
}

/**
 * Parse a voice note filename.
 * @param filename Attachment filename
 * @returns Parsed info, or `undefined` if this is not a voice note
 */
export function parseVoiceFilename(
  filename?: string | null,
): VoiceFileInfo | undefined {
  if (!filename) return undefined;
  const match = PATTERN.exec(filename);
  if (!match) return undefined;
  const peaks = match[3] ? decodePeaks(match[3]) : undefined;
  return {
    timestamp: Number(match[1]),
    durationMs: Number(match[2]),
    peaks: peaks?.length === VOICE_PEAK_COUNT ? peaks : undefined,
    extension: match[4],
  };
}

/**
 * Whether an attachment should render as a voice bubble: the `voz-` prefix
 * plus an audio container extension. Autumn sniffs audio-only WebM as
 * `video/webm`, so the metadata type is not reliable — the name is.
 * @param filename Attachment filename
 * @returns Whether it is a voice note
 */
export function isVoiceFilename(filename?: string | null): boolean {
  return (
    !!filename &&
    filename.startsWith(VOICE_PREFIX) &&
    /\.(webm|ogg|mp4|m4a)$/.test(filename)
  );
}

/**
 * Pick the MediaRecorder MIME type: Opus in WebM when available, else the
 * first supported candidate, else the browser default (empty string).
 * @param isTypeSupported `MediaRecorder.isTypeSupported`
 * @returns MIME type and the matching file extension
 */
export function pickRecorderMime(isTypeSupported: (type: string) => boolean): {
  mimeType: string;
  extension: string;
} {
  const candidates: [string, string][] = [
    ["audio/webm;codecs=opus", "webm"],
    ["audio/webm", "webm"],
    ["audio/ogg;codecs=opus", "ogg"],
    ["audio/mp4", "m4a"],
  ];
  for (const [mimeType, extension] of candidates) {
    try {
      if (isTypeSupported(mimeType)) return { mimeType, extension };
    } catch {
      // some engines throw on unknown types
    }
  }
  return { mimeType: "", extension: "webm" };
}
